import { useEffect } from 'react';
import './TriAttackAnimation.css';

export default function TriAttackAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-bg') || document.querySelector('.battle-bg');
    if (!container) {
      console.error('Battle background not found');
      onComplete();
      return;
    }

    const pRect = playerEl.getBoundingClientRect();
    const oRect = opponentEl.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();

    const startX = (pRect.left + pRect.width / 2) - cRect.left;
    const startY = (pRect.top + pRect.height / 2) - cRect.top;
    const endX = (oRect.left + oRect.width / 2) - cRect.left;
    const endY = (oRect.top + oRect.height / 2) - cRect.top;

    const elements = [];
    const timers = [];

    // Subtle source ring
    const ring = document.createElement('div');
    ring.className = 'tri-source-ring';
    ring.style.left = `${startX}px`;
    ring.style.top = `${startY}px`;
    container.appendChild(ring);
    elements.push(ring);

    // Spinning group at the player (rotor will rotate the triangle)
    const group = document.createElement('div');
    group.className = 'tri-orb-group';
    group.style.left = `${startX}px`;
    group.style.top = `${startY}px`;
    container.appendChild(group);
    elements.push(group);

    const rotor = document.createElement('div');
    rotor.className = 'tri-orb-rotor tri-spin-active';
    group.appendChild(rotor);

    const types = [
      { key: 'fire' },
      { key: 'ice' },
      { key: 'electric' },
    ];

    const spinRadius = 36; // px - more spacing between orbs
    const travelRadius = 42; // px - slightly wider radius during travel
    const launchDelay = 820; // allow a visible pre-launch spin
    const travelMs = 420; // longer travel to showcase the spin

    const spawnImpact = (type) => {
      // General impact ring
      const impact = document.createElement('div');
      impact.className = `tri-impact-ring tri-impact-ring--${type}`;
      impact.style.left = `${endX}px`;
      impact.style.top = `${endY}px`;
      container.appendChild(impact);
      elements.push(impact);
      timers.push(setTimeout(() => impact.remove(), 600));

      // Type-specific particles
      if (type === 'fire') {
        opponentEl.classList.add('tri-hit-fire', 'tri-hit-shake');
        timers.push(setTimeout(() => opponentEl.classList.remove('tri-hit-fire', 'tri-hit-shake'), 260));
        for (let i = 0; i < 14; i++) {
          const p = document.createElement('div');
          p.className = 'tri-fire-spark';
          p.style.left = `${endX}px`;
          p.style.top = `${endY}px`;
          const angle = Math.random() * Math.PI * 2;
          const dist = 24 + Math.random() * 34;
          p.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
          p.style.setProperty('--dy', `${Math.sin(angle) * dist * 0.8 - 6}px`);
          p.style.setProperty('--rot', `${(Math.random() * 60 - 30)}deg`);
          container.appendChild(p);
          elements.push(p);
          timers.push(setTimeout(() => p.remove(), 650));
        }
      } else if (type === 'ice') {
        opponentEl.classList.add('tri-hit-ice', 'tri-hit-shake');
        timers.push(setTimeout(() => opponentEl.classList.remove('tri-hit-ice', 'tri-hit-shake'), 260));
        for (let i = 0; i < 10; i++) {
          const shard = document.createElement('div');
          shard.className = 'tri-ice-shard';
          shard.style.left = `${endX}px`;
          shard.style.top = `${endY}px`;
          const angle = Math.random() * Math.PI * 2;
          const dist = 22 + Math.random() * 30;
          shard.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
          shard.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
          shard.style.setProperty('--angle', `${(angle * 180 / Math.PI)}deg`);
          container.appendChild(shard);
          elements.push(shard);
          timers.push(setTimeout(() => shard.remove(), 700));
        }
        // Frost mist
        for (let i = 0; i < 6; i++) {
          const mist = document.createElement('div');
          mist.className = 'tri-ice-mist';
          mist.style.left = `${endX}px`;
          mist.style.top = `${endY}px`;
          const angle = Math.random() * Math.PI * 2;
          const dist = 12 + Math.random() * 18;
          mist.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
          mist.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
          container.appendChild(mist);
          elements.push(mist);
          timers.push(setTimeout(() => mist.remove(), 620));
        }
      } else if (type === 'electric') {
        opponentEl.classList.add('tri-hit-electric', 'tri-hit-shake');
        timers.push(setTimeout(() => opponentEl.classList.remove('tri-hit-electric', 'tri-hit-shake'), 260));
        // Flash
        const flash = document.createElement('div');
        flash.className = 'tri-electric-flash';
        flash.style.left = `${endX}px`;
        flash.style.top = `${endY}px`;
        container.appendChild(flash);
        elements.push(flash);
        timers.push(setTimeout(() => flash.remove(), 250));
        // Sparks
        for (let i = 0; i < 10; i++) {
          const sp = document.createElement('div');
          sp.className = 'tri-electric-spark';
          sp.style.left = `${endX}px`;
          sp.style.top = `${endY}px`;
          const angle = Math.random() * Math.PI * 2;
          const dist = 20 + Math.random() * 26;
          sp.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
          sp.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
          sp.style.setProperty('--angle', `${(Math.random() * 60 - 30)}deg`);
          container.appendChild(sp);
          elements.push(sp);
          timers.push(setTimeout(() => sp.remove(), 520));
        }
      }
    };

    // Create 3 elemental orbs positioned as triangle vertices (children of rotor)
    // Use an anchor wrapper so position (rotate/translate) and inner orb scale don't conflict
    const orbs = types.map((t, i) => {
      const anchor = document.createElement('div');
      anchor.className = 'tri-orb-anchor';
      anchor.style.setProperty('--angle', `${i * 120}deg`);
      anchor.style.setProperty('--radius', `${spinRadius}px`);
      rotor.appendChild(anchor);
      elements.push(anchor);

      const orb = document.createElement('div');
      orb.className = `tri-orb tri-orb--${t.key} tri-orb--charge`;
      anchor.appendChild(orb);
      elements.push(orb);

      return { t: t.key, anchor, orb, angleDeg: i * 120 };
    });

    // Launch the orbs toward the opponent
    const launchTimer = setTimeout(() => {
      // switch to faster travel spin and widen spacing while traveling
      rotor.classList.remove('tri-spin-active');
      rotor.classList.add('tri-spin-travel');
      orbs.forEach(({ anchor }) => {
        anchor.style.setProperty('--radius', `${travelRadius}px`);
      });

      // animate the entire group to the target while rotor spins -> orbs spin around each other in-flight
      group.classList.add('tri-group--travel');
      group.style.setProperty('--dx', `${endX - startX}px`);
      group.style.setProperty('--dy', `${endY - startY}px`);

      // Trigger three staggered elemental impacts upon arrival
      types.forEach((t, i) => {
        const it = setTimeout(() => spawnImpact(t.key), travelMs + i * 80);
        timers.push(it);
      });
    }, launchDelay);
    timers.push(launchTimer);

    // Completion
    const completeTimer = setTimeout(() => {
      timers.forEach(clearTimeout);
      elements.forEach(el => { if (el && el.isConnected) el.remove(); });
      opponentEl.classList.remove('tri-hit-fire', 'tri-hit-ice', 'tri-hit-electric', 'tri-hit-shake');
      onComplete();
    }, launchDelay + travelMs + 900);
    timers.push(completeTimer);

    return () => {
      timers.forEach(clearTimeout);
      elements.forEach(el => { if (el && el.isConnected) el.remove(); });
      opponentEl.classList.remove('tri-hit-fire', 'tri-hit-ice', 'tri-hit-electric', 'tri-hit-shake');
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

TriAttackAnimation.moveType = 'normal';
